import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { addYears, isFuture } from 'date-fns';
import { find } from 'lodash';
import { useSelector } from 'react-redux';
import handleChange from '../../../helpers/formInputHelpers';
import { getMenuLabelWithTranslation } from '../../../helpers/selectHelpers';
import useRegisterValidationFields from '../../../hooks/useRegisterValidationFields';
import { isArmenianCountry } from '../../../helpers/countryHelper';
import { checkIsAdult } from '../../../helpers/dates';

const PassportIdInfoWrapper = ({
  formProps,
  onCitizenshipChange,
  initialData,
  socCardEnabled,
  children,
}) => {
  const { i18n } = useTranslation();
  const { register, setValue, triggerValidation, getValues } = formProps;
  const [issueDate, setIssueDate] = useState();
  const [validityDate, setValidityDate] = useState();
  const [citizenship, setCitizenship] = useState(null);
  const { countries } = useSelector((store) => store.countriesReducer);
  const [passportIdInfo, setPassportIdInfo] = useState({
    passport: '',
    passportFrom: '',
    socCard: '',
  });

  useEffect(() => {
    setPassportIdInfo(() => ({
      passport: initialData.passport || '',
      passportFrom: initialData.passportFrom || '',
      socCard: initialData.socCard || '',
    }));
  }, [initialData.passport, initialData.passportFrom, initialData.socCard]);

  useRegisterValidationFields(['citizenship', 'passportGiven', 'passportValidUntil', 'passportFrom', 'passport', 'socCard'], register);

  const handleIssueDateChange = useCallback(
    (newDate) => {
      setIssueDate(newDate);
      setValue('passportGiven', newDate, true);

      if (isArmenianCountry(citizenship) && newDate
          && ((getValues().birthday && checkIsAdult(getValues().birthday)) || !getValues().birthday)
      ) {
        const vDate = addYears(newDate, 10);

        if (isFuture(vDate)) {
          setValidityDate(vDate);
          setValue('passportValidUntil', vDate, true);
        }
      }

      if (validityDate) {
        triggerValidation({ name: 'passportValidUntil' });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setValue, citizenship, getValues().birthday, validityDate, triggerValidation],
  );

  useEffect(() => {
    setValue(
      'passport',
      initialData.passport || '', // here added '' to avoid {undefined} value
      Boolean(initialData.passport),
    );
    setValue('passportFrom', initialData.passportFrom || '', Boolean(initialData.passportFrom));

    if (initialData.passportGiven) {
      setIssueDate(initialData.passportGiven);
      setValue('passportGiven', initialData.passportGiven, true);
    } else {
      setIssueDate(null);
      setValue('passportGiven', null);
    }
    if (initialData.passportValidUntil && isFuture(new Date(initialData.passportValidUntil))) {
      setValidityDate(initialData.passportValidUntil);
      setValue('passportValidUntil', initialData.passportValidUntil, true);
    } else {
      setValidityDate(null);
      setValue('passportValidUntil', null);
    }
    setValue('socCard', initialData.socCard || '', Boolean(initialData.socCard));
  }, [initialData, setValue]);

  const handleCitizenshipChange = useCallback(
    (country, validate) => {
      setCitizenship(country);
      setValue('citizenship', country, validate);
      onCitizenshipChange(country);

      if (getValues().passportFrom) {
        triggerValidation({ name: 'passportFrom' });
      }
      if (getValues().passport) {
        triggerValidation({ name: 'passport' });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onCitizenshipChange, setValue],
  );

  useEffect(() => {
    if (initialData.citizenship) {
      const country = find(countries, (c) => c.id === initialData.citizenship.id);
      if (country) {
        handleCitizenshipChange(country);
      }
    } else {
      handleCitizenshipChange(countries[0]);
    }
  }, [countries, onCitizenshipChange, initialData, setValue, handleCitizenshipChange]);

  const handleChangeInput = useCallback(
    (key, value) => handleChange(key, value, setPassportIdInfo, setValue),
    [setValue],
  );

  const getLabelWithTranslation = useCallback(
    (option) => getMenuLabelWithTranslation(option, i18n),
    [i18n],
  );

  return (
    <>
      {
        React.cloneElement(children, {
          formProps,
          citizenship,
          countries,
          getLabelWithTranslation,
          handleCitizenshipChange,
          socCardEnabled,
          passportIdInfo,
          handleChangeInput,
          issueDate,
          validityDate,
          handleIssueDateChange,
          setValidityDate,
        })
      }
    </>
  );
};

PassportIdInfoWrapper.propTypes = {
  initialData: PropTypes.shape({
    passport: PropTypes.string,
    passportFrom: PropTypes.string,
    passportGiven: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
    passportValidUntil: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
    citizenship: PropTypes.shape({
      id: PropTypes.number,
    }),
    resident: PropTypes.number,
    socCard: PropTypes.string,
  }).isRequired,
  formProps: PropTypes.shape({
    register: PropTypes.func,
    setValue: PropTypes.func,
    getValues: PropTypes.func,
    triggerValidation: PropTypes.func,
    errors: PropTypes.shape({
      passportValidUntil: PropTypes.shape({}),
    }),
    clearError: PropTypes.func,
  }).isRequired,
  onCitizenshipChange: PropTypes.func,
  socCardEnabled: PropTypes.bool,
  children: PropTypes.element,
};

PassportIdInfoWrapper.defaultProps = {
  onCitizenshipChange: () => {},
  socCardEnabled: false,
  children: undefined,
};

export default PassportIdInfoWrapper;
